import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import ConstanteFormBase from "../constante/ConstanteFormBase";
import { useConstanteData } from "../../hooks/useConstanteData";
import { X } from "lucide-react";
import type { ConstanteFormData } from "../../types";

const ConstanteUpdate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { constante, loading, error } = useConstanteData("admin", id);
  const [responseMessage, setResponseMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set filtered patients and selected patient name when data loads

  const handleSubmit = async (data: ConstanteFormData) => {
    setIsSubmitting(true);
    setResponseMessage("");
    const payload = {
      weight: data.poids ? parseFloat(data.poids) : null,
      height: data.taille ? parseFloat(data.taille) : null,
      bloodPressure: data.tensionArterielle || null,
      heartRate: data.frequenceCardiaque
        ? parseInt(data.frequenceCardiaque)
        : null,
      temperature: data.temperature ? parseFloat(data.temperature) : null,
      reason: data.motif || null,
      status: data.status || "PENDING",
      recordedAt: constante?.recordedAt || new Date().toISOString(),
      doctorId: data.doctorId ? parseInt(String(data.doctorId)) : null,
      patientId: data.maladeId ? parseInt(String(data.maladeId)) : null,
      medicalRecordId: data.dossierMedicalId || null,
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");
      await axiosInstance.put(
        API_ENDPOINTS.CONSTANTES.UPDATE(Number(id)),
        payload
      );
      setResponseMessage("Constantes médicales mises à jour avec succès !");
      setTimeout(() => navigate("/admin/dashboard/constante"), 1500);
    } catch (err) {
      console.error(err);
      setResponseMessage("Erreur lors de la mise à jour des constantes.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <LoadingScreen message="Préparation du formulaire..." role="admin" />
    );
  if (error || !constante)
    return (
      <ResponseMessage
        message={error || "Constante non trouvée"}
        role="admin"
      />
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Modifier les Constantes Médicales"
        description="Mettez à jour les informations des constantes médicales pour le patient."
        action={{
          label: "Annuler",
          to: "/admin/dashboard/constante",
          icon: <X className="w-4 h-4" />,
        }}
        role="admin"
      />
      <section className="px-6">
        <ConstanteFormBase
          mode="update"
          role="admin"
          initialData={constante}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
        {responseMessage && (
          <ResponseMessage message={responseMessage} role="admin" />
        )}
      </section>
    </div>
  );
};

export default ConstanteUpdate;
